import { useEffect } from "react"
import { EntryClient } from "@/domain/entry/client/EntryClient"
import { PHONE_NO_PATTERN } from "@/domain/entry/model/Entry"
import {
  EntryLookupStore,
  makeInitEntryLookupState,
} from "@/domain/entry/store/EntryLookupStore"

/**
 * 참가 신청 조회 훅.
 *
 * 인증이 없어 체육관 연락처 + 신청 비밀번호가 함께 맞아야 서버가 내려준다.
 * 목록을 먼저 받고(신청번호를 몰라도 된다), 한 건을 고르면 상세를 받는다.
 */
export function useEntryLookup() {
  function useEntryLookupState() {
    return EntryLookupStore.useStore()
  }

  function setGymContactPhoneNo(gymContactPhoneNo: string) {
    EntryLookupStore.set("gymContactPhoneNo", gymContactPhoneNo)
  }

  function setPassword(password: string) {
    EntryLookupStore.set("password", password)
  }

  function closeDetail() {
    EntryLookupStore.set("detail", null)
  }

  function validate(): string {
    const state = EntryLookupStore.store.getState()

    if (!PHONE_NO_PATTERN.test(state.gymContactPhoneNo.replaceAll(" ", ""))) {
      return "체육관 연락처 형식을 확인해 주세요."
    }
    if (!state.password) {
      return "신청 비밀번호를 입력해 주세요."
    }

    return ""
  }

  /** 연락처 + 비밀번호로 신청 목록을 받는다 */
  async function search() {
    const validationMessage = validate()

    if (validationMessage) {
      EntryLookupStore.set("summaries", [])
      EntryLookupStore.set("detail", null)
      EntryLookupStore.set("errorMessage", validationMessage)
      EntryLookupStore.set("isSearched", true)
      return
    }

    const state = EntryLookupStore.store.getState()
    EntryLookupStore.set("isLoading", true)

    try {
      const summaries = await EntryClient.getByGym({
        gymContactPhoneNo: state.gymContactPhoneNo,
        password: state.password,
      })
      EntryLookupStore.set("summaries", summaries)
      EntryLookupStore.set("detail", null)
      EntryLookupStore.set("errorMessage", "")
    } catch (error) {
      EntryLookupStore.set("summaries", [])
      EntryLookupStore.set("detail", null)
      EntryLookupStore.set(
        "errorMessage",
        error instanceof Error
          ? error.message
          : "신청 내역을 불러오지 못했습니다.",
      )
    } finally {
      EntryLookupStore.set("isSearched", true)
      EntryLookupStore.set("isLoading", false)
    }
  }

  /** 목록에서 고른 신청의 상세를 받는다 */
  async function openDetail(entryNo: string) {
    const state = EntryLookupStore.store.getState()
    EntryLookupStore.set("isLoading", true)

    try {
      const detail = await EntryClient.getByEntryNo({
        entryNo,
        gymContactPhoneNo: state.gymContactPhoneNo,
        password: state.password,
      })
      EntryLookupStore.set("detail", detail)
      EntryLookupStore.set("errorMessage", "")
    } catch (error) {
      EntryLookupStore.set("detail", null)
      EntryLookupStore.set(
        "errorMessage",
        error instanceof Error
          ? error.message
          : "신청 내역을 불러오지 못했습니다.",
      )
    } finally {
      EntryLookupStore.set("isLoading", false)
    }
  }

  async function cancelEntry(entryNo: string, reason: string) {
    const state = EntryLookupStore.store.getState()
    EntryLookupStore.set("isLoading", true)

    try {
      await EntryClient.cancel({
        entryNo,
        gymContactPhoneNo: state.gymContactPhoneNo,
        password: state.password,
        reason,
      })
      EntryLookupStore.set("errorMessage", "")
      await search()
      await openDetail(entryNo)
    } catch (error) {
      EntryLookupStore.set(
        "errorMessage",
        error instanceof Error ? error.message : "신청 취소에 실패했습니다.",
      )
    } finally {
      EntryLookupStore.set("isLoading", false)
    }
  }

  useEffect(() => {
    EntryLookupStore.store.setState(makeInitEntryLookupState())

    return () => {
      EntryLookupStore.store.setState(makeInitEntryLookupState())
    }
  }, [])

  return {
    useEntryLookupState,
    setGymContactPhoneNo,
    setPassword,
    search,
    openDetail,
    closeDetail,
    cancelEntry,
  }
}
